import type {
    AutoSizedLabelText,
    BakedLabelSource,
    BarPlacedLabelDatum,
    BarValueAnchor,
    BoxBounds,
    Callback,
    CallbackParam,
    CandidateLabelStyle,
    CandidateStyleResolver,
    CollideWith,
    DynamicContext,
    FitRegion,
    FontOptions,
    IsAny,
    LabelFit,
    LabelFitDescriptor,
    LabelPlacement,
    NormalisedColorType,
    NormalisedTextOrSegments,
    OrientationAnchor,
    Point,
    PointLabelDatum,
    PositionedCandidateResolver,
    PositionedLabelCandidate,
    RectObstacleSource,
    RegionAlign,
} from 'ag-charts-core';
import {
    type NormalisedChartLabelStyleOptions,
    barLabelObstacles,
    fitLabelTextOrOverflow,
    fitLabelTextOrOverflowAutoSize,
    fontWithSize,
    getMinOuterRectSize,
    insetFitRegion,
    insideBarContainer,
    insideBarRegion,
    insideBarValueInsets,
    measureLabelText,
    mergeDefaults,
    orientationAngles,
    resolveLabelFitDescriptors,
    rotatedGlyphDrift,
    rotatedLabelInset,
    sectorLabelContainer,
    writeLabelBoxCentre,
} from 'ag-charts-core';
import type {
    AgChartLabelOrientation,
    AgChartLabelStylerParams,
    AgConeFunnelSeriesLabelPlacement,
    AgFunnelSeriesLabelPlacement,
    AgMarkerShape,
    CssColor,
    HighlightState,
    NormalisedCallbackParams,
    PaddingOptions,
    PixelSize,
    SelectionState,
} from 'ag-charts-types';

import type { HighlightNodeDatum } from '../core/eventsHub';
import type { ChartRegistry } from '../module/moduleContext';
import type { Text } from '../scene/shape/text';
import { isRotatable } from '../scene/transformable';
import { type SectorBoundaries, isBoxInSector, isPointInSector } from '../scene/util/sector';
import {
    type Label,
    type LabelPlacementStyle,
    expandLabelBoxExtent,
    expandPlacementLabelBoxExtent,
    resolvePlacementLabelBoxExtent,
    resolvePlacementLabelStyle,
} from './label';
import { markerLabelRect } from './marker/markerLabelRect';
import { getItemId } from './series/pickManager';
import type { DatumIndex, SeriesNodeDatum } from './series/seriesTypes';

interface SeriesLike {
    id: string;
    ctx: DynamicContext<ChartRegistry>;
    declarationOrder: number;
    readonly data?: { readonly dataIdKey?: string };
    get visible(): boolean;
    cachedCallWithContext<F extends Callback>(fn: F, params: CallbackParam<F>): ReturnType<F> | undefined;
    isSeriesHighlighted(highlightedDatum: HighlightNodeDatum | undefined): boolean;
    getHighlightStateString(
        datum: HighlightNodeDatum | undefined,
        isHighlight?: boolean,
        datumIndex?: DatumIndex
    ): HighlightState;
    getSelectionStateString(datumIndex: DatumIndex | undefined): SelectionState | undefined;
    getCandidateStateString(datumIndex: DatumIndex | undefined): SelectionState | undefined;
}

type Bounds = {
    x: number;
    y: number;
    width: number;
    height: number;
};

/** Bar `beside-*` placements offset a label perpendicular to the value axis, to the side of its segment. */
export type BesideBarLabelPlacement = `beside-${'before' | 'after'}-${'start' | 'center' | 'end'}`;

export type BarLabelPlacement =
    | 'inside-center'
    | 'inside-start'
    | 'inside-end'
    | 'outside-start'
    | 'outside-end'
    | BesideBarLabelPlacement;

/** A label's resolved inside/outside placement, selecting which placement-style overrides apply. */
export type ResolvedLabelPlacement = 'inside' | 'outside';

/** The final placement/orientation a series chose for a label, surfaced to `itemStyler` params. */
type ResolvedPlacement = Pick<AgChartLabelStylerParams<unknown, unknown>, 'placement' | 'orientation'>;

type LabelDatum = Point & {
    text: NormalisedTextOrSegments;
    /**
     * The text the placement engine fitted to the candidate it chose, rendered in place of {@link text}.
     * Kept separate so {@link text} stays the unfitted source every placement pass re-fits from, rather
     * than each pass truncating the previous pass's output further.
     */
    fittedText?: NormalisedTextOrSegments;
    /** Reduced font size the text was fitted at, overriding the styled `fontSize` when the label shrank. */
    fittedFontSize?: number;
    textAlign: CanvasTextAlign;
    textBaseline: CanvasTextBaseline;
    /**
     * The label's resolved placement. Bar-family labels carry the granular {@link BarLabelPlacement}
     * (coarsened to inside/outside via {@link toResolvedPlacement} when selecting placement styles);
     * placement-engine series carry the compass {@link LabelPlacement}; the rest carry the coarse
     * {@link ResolvedLabelPlacement}. Unset applies neither style.
     */
    placement?:
        | ResolvedLabelPlacement
        | BarLabelPlacement
        | LabelPlacement
        | AgFunnelSeriesLabelPlacement
        | AgConeFunnelSeriesLabelPlacement;
    /** Rotation in radians applied to the label node; `undefined`/`0` renders upright. */
    rotation?: number;
    /** Translation (px) sliding a region-bound label flush inside its region; `undefined`/`0` leaves it anchored. */
    offsetX?: number;
    offsetY?: number;
};

/**
 * Fits a label's text to its resolved policy and, when the series supplies one, its geometric container
 * (bar rect, donut hole, …). Each axis is bounded by the tighter of the explicit `maxWidth`/`maxHeight`
 * and the container extent, so text never overflows its container yet the user can constrain further.
 * When the policy resolves to "show" (no truncation and no collision avoidance) no bound is applied and
 * the text renders in full, leaving container series unchanged unless the user opts into truncation.
 */
export function fitLabelToContainer(
    text: NormalisedTextOrSegments,
    fit: LabelFit | undefined,
    font: FontOptions,
    container: { width: number; height: number } | undefined,
    fitOverflow?: LabelFit
): NormalisedTextOrSegments {
    return fitLabelTextOrOverflow(text, boundLabelFit(fit, container), fitOverflow, font);
}

/**
 * {@link fitLabelToContainer} for a label that may shrink, reporting the reduced size its text was fitted
 * at so the caller can measure and render it there; `fontSize` is `undefined` at the configured size.
 */
export function fitLabelToContainerAutoSize(
    text: NormalisedTextOrSegments,
    fit: LabelFit | undefined,
    font: FontOptions,
    container: { width: number; height: number } | undefined,
    fitOverflow?: LabelFit
): AutoSizedLabelText {
    return fitLabelTextOrOverflowAutoSize(text, boundLabelFit(fit, container), fitOverflow, font);
}

/**
 * Bounds a fit's `maxWidth`/`maxHeight` by a container extent, taking the tighter of the explicit bound
 * and the container. Returns `fit` unchanged when there is no fit or no container. Series with an
 * invariant container should call this once at context-build time rather than per datum.
 */
export function boundLabelFit(
    fit: LabelFit | undefined,
    container: { width: number; height: number } | undefined
): LabelFit | undefined {
    if (fit == null || container == null) {
        return fit;
    }
    return {
        ...fit,
        maxWidth: Math.min(fit.maxWidth ?? Infinity, container.width),
        maxHeight: Math.min(fit.maxHeight ?? Infinity, container.height),
    };
}

/**
 * Container that keeps an `inside` label within a marker of diameter `markerSize`, sized to the largest
 * rectangle that fits the marker's shape (analysed once per shape by {@link markerLabelRect}). `threshold`
 * shrinks that rectangle on every side so the label clears the marker walls by that many pixels. Pair
 * with {@link resolveInsidePlacement}'s `offset` to position the label at that rectangle, which need not
 * be marker-centred.
 */
export function insideMarkerContainer(
    markerSize: number,
    shape?: AgMarkerShape,
    threshold = 0
): { width: number; height: number } {
    const rect = markerLabelRect(shape);
    return {
        width: Math.max(0, markerSize * rect.width - 2 * threshold),
        height: Math.max(0, markerSize * rect.height - 2 * threshold),
    };
}

/** Placement and size of the rectangle a horizontal sector label fits into. */
export interface SectorLabelRect {
    /** Centre of the inscribed rectangle — where the horizontal label sits. */
    readonly centerX: number;
    readonly centerY: number;
    /** Room the label may use at that centre: the wedge's own width there, not the text's. */
    readonly width: number;
    /** The block's height — as measured by the caller when it supplied one, estimated from one line if not. */
    readonly height: number;
    /** The block fits centred on the anchor, so a caller must not recentre it on the room it found. */
    readonly anchored?: boolean;
}

/** Size a sector label's text actually wrapped to, as measured on the node that will draw it. */
export interface BlockSize {
    readonly width: number;
    readonly height: number;
}

// A box taller than one line by this ratio is treated as multi-line and fitted to a horizontal band
// (see centreSectorLabelInBand) rather than slid symmetrically about the bisector.
const SECTOR_MULTILINE_HEIGHT_RATIO = 1.5;
// Binary-search iterations for the sector edge probes below; ~24 halvings resolves sub-pixel on any radius.
const SECTOR_BISECTION_STEPS = 24;

/** Largest `t` in `[0, limit]` for which `inside(t)` still holds, by binary search (`inside` is monotone false-ward). */
function furthestInside(limit: number, inside: (t: number) => boolean): number {
    let lo = 0;
    let hi = limit;
    for (let i = 0; i < SECTOR_BISECTION_STEPS; i += 1) {
        const t = (lo + hi) / 2;
        if (inside(t)) {
            lo = t;
        } else {
            hi = t;
        }
    }
    return lo;
}

/**
 * Rectangle a horizontal label should fill inside an origin-centred annular wedge. {@link sectorLabelContainer}
 * sizes a box that fits the wedge centred on `anchor`, but a horizontal label is not symmetric about the sector
 * bisector, so that box hugs one side. Keeping the (safe) size, we slide it to the middle of the room the wedge
 * actually offers on each axis — probed with {@link isPointInSector} — so the label sits centred both ways
 * instead of against an edge. `lineHeight` seeds the size search with a single line's height.
 *
 * A multi-line box spans a radial band whose width varies with height, so sizing it symmetrically about the
 * bisector caps its width at the nearer radial edge and the slide above cannot recover the room the far side
 * offers — the label ends up hugging one edge. Such a box is instead fitted to the wedge's true horizontal
 * extent across its own band (see {@link centreSectorLabelInBand}); the symmetric slide is kept for
 * single-line boxes, whose thin band cannot exhibit this.
 *
 * `block` replaces the estimated size once the caller knows what its text wrapped to: a block sized from one
 * line is placed where a thin box fits, which is not where the block it grew into belongs. A block that fits
 * on the anchor stays on it — the sector nominates that point for its label, and both searches below trade
 * centring for room a block this size does not need.
 */
export function fitSectorLabelRect(
    anchor: Point,
    sector: { startAngle: number; endAngle: number; innerRadius: number; outerRadius: number },
    lineHeight: number,
    block?: BlockSize
): SectorLabelRect {
    const container = sectorLabelContainer(anchor, sector, lineHeight);
    const width = container.width;
    const height = block?.height ?? container.height;
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    if (halfWidth <= 0 || halfHeight <= 0 || !isPointInSector(anchor.x, anchor.y, sector)) {
        return { centerX: anchor.x, centerY: anchor.y, width, height };
    }
    if (block != null && fitsOnAnchor(anchor, sector, block)) {
        return { centerX: anchor.x, centerY: anchor.y, width, height, anchored: true };
    }
    if (height > lineHeight * SECTOR_MULTILINE_HEIGHT_RATIO) {
        const centred = centreSectorLabelInBand(anchor, sector, height, block?.width);
        if (centred != null) {
            return centred;
        }
    }
    const contains = (x: number, y: number) => isPointInSector(x, y, sector);
    const limit = Math.abs(sector.outerRadius) * 2;
    // Furthest an edge through corners A and B can slide along (dirX, dirY) while both corners stay in the wedge.
    const reach = (ax: number, ay: number, bx: number, by: number, dirX: number, dirY: number) =>
        furthestInside(limit, (t) => contains(ax + dirX * t, ay + dirY * t) && contains(bx + dirX * t, by + dirY * t));
    // Clearance for the box's leading edges (kept at the final size), then recentre on the room found.
    const right = reach(anchor.x, anchor.y - halfHeight, anchor.x, anchor.y + halfHeight, 1, 0);
    const left = reach(anchor.x, anchor.y - halfHeight, anchor.x, anchor.y + halfHeight, -1, 0);
    const centerX = anchor.x + (right - left) / 2;
    const down = reach(centerX - halfWidth, anchor.y, centerX + halfWidth, anchor.y, 0, 1);
    const up = reach(centerX - halfWidth, anchor.y, centerX + halfWidth, anchor.y, 0, -1);
    return { centerX, centerY: anchor.y + (down - up) / 2, width, height };
}

// Radial slack (px) kept between the placed box and the wedge's arcs, so a box corner rounded outward by
// floating error still passes the visibility gate that hides overflowing sector labels.
const SECTOR_ARC_INSET = 0.75;
// Two bands whose widths are within this many px count as equally wide, so the tie-break toward the band
// furthest from the centre decides between them rather than sub-pixel noise.
const SECTOR_WIDTH_TOLERANCE = 2;
const BAND_STEPS = 48;

/** Sector shrunk radially by {@link SECTOR_ARC_INSET}, so a box it accepts also clears the drawn arcs. */
function arcInsetSector(sector: SectorBoundaries): SectorBoundaries {
    return {
        startAngle: sector.startAngle,
        endAngle: sector.endAngle,
        innerRadius: sector.innerRadius > 0 ? sector.innerRadius + SECTOR_ARC_INSET : 0,
        outerRadius: Math.abs(sector.outerRadius) - SECTOR_ARC_INSET,
    };
}

/** True when a box of `block`'s size centred on `anchor` lies inside the wedge, clear of its arcs. */
function fitsOnAnchor(anchor: Point, sector: SectorBoundaries, { width, height }: BlockSize) {
    const box = { x: anchor.x - width / 2, y: anchor.y - height / 2, width, height };
    return isBoxInSector(box, arcInsetSector(sector));
}

/**
 * The wedge's horizontal extent at each of {@link BAND_STEPS} vertical centres, for a band `height` tall:
 * the box at each spans the extent common to the band's top and bottom edges (convex wedge, so those edges
 * bind) searched outward from the bisector with {@link isPointInSector}. Seeded from the bisector, so an
 * anchor on the chart's centre has no ray to seed from and must be excluded by the caller.
 */
function sectorBands(anchor: Point, inset: SectorBoundaries, height: number) {
    const radius = Math.hypot(anchor.x, anchor.y);
    const midCos = anchor.x / radius;
    const midSin = anchor.y / radius;
    const outer = inset.outerRadius;
    const halfHeight = height / 2;
    const limit = outer * 2;
    // Furthest the bisector-seed on line `y` can slide along `dir` (±1 in x) while staying in the wedge.
    const edge = (seedX: number, y: number, dir: -1 | 1) =>
        seedX + dir * furthestInside(limit, (t) => isPointInSector(seedX + dir * t, y, inset));
    // Seed each line from the point where the bisector ray crosses it, guaranteed inside where the ray reaches.
    const seedAt = (y: number) => (Math.abs(midSin) > 1e-3 ? (y * midCos) / midSin : anchor.x);

    const bands: SectorLabelRect[] = [];
    let maxWidth = 0;
    for (let i = 0; i <= BAND_STEPS; i += 1) {
        const centerY = -outer + (2 * outer * i) / BAND_STEPS;
        let left = -Infinity;
        let right = Infinity;
        let fits = true;
        for (const y of [centerY - halfHeight, centerY + halfHeight]) {
            const seedX = seedAt(y);
            if (!isPointInSector(seedX, y, inset)) {
                fits = false;
                break;
            }
            left = Math.max(left, edge(seedX, y, -1));
            right = Math.min(right, edge(seedX, y, 1));
        }
        if (!fits || right <= left) {
            continue;
        }
        const width = right - left;
        bands.push({ centerX: (left + right) / 2, centerY, width, height });
        maxWidth = Math.max(maxWidth, width);
    }
    return { bands, maxWidth };
}

/** Bounds on the text a wedge can hold, for a caller sizing a label before it lays any text out. */
export interface SectorLabelRoom {
    /** Total room the wedge's bands add up to. */
    readonly area: number;
    /** Widest total advance the wedge could hold at `lineHeight`, stacking as many whole lines as it fits. */
    capacityAt(lineHeight: number): number;
}

/**
 * The room a wedge offers a label, measured band by band for a band `lineHeight` tall. Both bounds are
 * generous — a band is wider than the text centred in it, wrapping on words costs width they do not charge
 * for, and a band measured for a short line is never narrower than a taller line would get — so text
 * exceeding either cannot fit however it wraps, which is what makes them safe to reject a font size on.
 */
export function sectorLabelRoom(anchor: Point, sector: SectorBoundaries, lineHeight: number): SectorLabelRoom {
    if (Math.hypot(anchor.x, anchor.y) < 1e-6) {
        // No bisector to seed the bands from, so the wedge goes unmeasured and no font size is rejected.
        return { area: Infinity, capacityAt: () => Infinity };
    }
    const inset = arcInsetSector(sector);
    const { bands } = sectorBands(anchor, inset, lineHeight);
    const step = (2 * inset.outerRadius) / BAND_STEPS;
    const widest = bands.map((band) => band.width).toSorted((a, b) => b - a);
    return {
        area: widest.reduce((total, width) => total + width * step, 0),
        capacityAt: (height: number) =>
            widest.slice(0, Math.floor((widest.length * step) / height)).reduce((total, width) => total + width, 0),
    };
}

/**
 * Fits a multi-line sector label to one of the wedge's bands. A tall box measured symmetrically about the
 * bisector is capped by the nearer radial edge and left hugging it, so a band — centred between the wedge's
 * sides — is chosen instead. Returns `null` when no band holds the box (e.g. one nearly as tall as the
 * radius), so the caller falls back to symmetric placement.
 *
 * Of the bands that hold `blockWidth` — the width the text actually wrapped to — the one nearest `anchor`
 * wins, so a label sits on the point the sector nominates for it and moves only as far as it must to fit.
 * Without a wrapped width to answer to, the widest band wins instead, the furthest out among equals.
 */
function centreSectorLabelInBand(
    anchor: Point,
    sector: { startAngle: number; endAngle: number; innerRadius: number; outerRadius: number },
    height: number,
    blockWidth?: number
): SectorLabelRect | null {
    const radius = Math.hypot(anchor.x, anchor.y);
    if (radius < 1e-6) {
        return null;
    }
    // Probe against arc-inset radii so the chosen box clears the arcs by SECTOR_ARC_INSET on both sides.
    const { bands, maxWidth } = sectorBands(anchor, arcInsetSector(sector), height);
    if (blockWidth != null) {
        const nearest = nearestBandHolding(bands, anchor, Math.min(blockWidth, maxWidth) - SECTOR_WIDTH_TOLERANCE);
        if (nearest != null) {
            return nearest;
        }
    }
    // Among the widest bands (within a tolerance), take the one furthest from the chart centre.
    let best: SectorLabelRect | null = null;
    let bestRadius = -Infinity;
    for (const band of bands) {
        if (band.width < maxWidth - SECTOR_WIDTH_TOLERANCE) {
            continue;
        }
        const bandRadius = Math.hypot(band.centerX, band.centerY);
        if (bandRadius > bestRadius) {
            best = band;
            bestRadius = bandRadius;
        }
    }
    return best;
}

/** Band at least `required` wide whose centre lies nearest `anchor`; `null` when none is wide enough. */
function nearestBandHolding(bands: SectorLabelRect[], anchor: Point, required: number): SectorLabelRect | null {
    let best: SectorLabelRect | null = null;
    let bestDistance = Infinity;
    for (const band of bands) {
        if (band.width < required) {
            continue;
        }
        const distance = Math.hypot(band.centerX - anchor.x, band.centerY - anchor.y);
        if (distance < bestDistance) {
            best = band;
            bestDistance = distance;
        }
    }
    return best;
}

/** Inside-marker label geometry resolved from a series' configured placements and marker shape. */
export interface InsidePlacement {
    /** True when every placement is `inside`, so the text is fitted to the marker up front. */
    readonly insideOnly: boolean;
    /** Centres an `inside` label on the marker's inscribed rectangle; set whenever `inside` is a placement. */
    readonly offset: Point | undefined;
    /** Inscribed-rect size so the engine rejects an oversized `inside` candidate; set only for a mixed list. */
    readonly size: { width: number; height: number } | undefined;
}

/**
 * Resolves the inside-marker geometry for a placement list. A mixed list (e.g. `['inside','top']`) keeps
 * full-size text and instead supplies `size`, so an `inside` candidate too large for the marker fails and
 * cascades to the next placement; an inside-only list leaves `size` unset and fits the text to the marker.
 */
export function resolveInsidePlacement(
    placements: readonly LabelPlacement[],
    shape: AgMarkerShape | undefined
): InsidePlacement {
    const insideOnly = placements.length > 0 && placements.every((placement) => placement === 'inside');
    const rect = placements.includes('inside') ? markerLabelRect(shape) : undefined;
    return {
        insideOnly,
        offset: rect ? { x: rect.cx, y: rect.cy } : undefined,
        size: !insideOnly && rect ? { width: rect.width, height: rect.height } : undefined,
    };
}

/** Selects the style overrides for a label's resolved placement; `undefined` when neither applies. */
export function pickPlacementStyle(
    styles: { insideStyle: LabelPlacementStyle; outsideStyle: LabelPlacementStyle } | undefined,
    placement: ResolvedLabelPlacement | undefined
): LabelPlacementStyle | undefined {
    if (styles == null || placement == null) return undefined;
    return placement === 'inside' ? styles.insideStyle : styles.outsideStyle;
}

/** Coarsens a granular bar placement to the inside/outside distinction the placement styles select on. */
export function toResolvedPlacement(placement: BarLabelPlacement): ResolvedLabelPlacement {
    return placement.startsWith('inside') ? 'inside' : 'outside';
}

/** Coarsens a compass placement the same way the render path does: only `inside` sits on the shape. */
function toResolvedCompassPlacement(placement: LabelPlacement | undefined): ResolvedLabelPlacement {
    return placement === 'inside' ? 'inside' : 'outside';
}

/** A label surface carrying the placement-reactive style overrides a candidate style resolves against. */
type PlacementStyledLabel<TParams> = Label<TParams> & {
    insideStyle: LabelPlacementStyle;
    outsideStyle: LabelPlacementStyle;
};

/**
 * How one candidate's compass placement reads: `style` selects the placement-style overrides, `reported`
 * is the `placement` the styler params carry. They differ for series that surface the compass placement
 * itself (line, area, bubble) rather than a coarsened one (range-area's inside/outside band sides).
 */
export interface CandidatePlacement {
    readonly style: ResolvedLabelPlacement;
    readonly reported: ResolvedPlacement['placement'];
}

/** Maps a candidate's compass placement onto the style and reported placement for `datum`. */
export type CandidatePlacementMapper = (
    placement: LabelPlacement | undefined,
    datum: PointLabelDatum
) => CandidatePlacement;

/** Compass series report the placement they were given; only `inside` sits on the shape. */
export const compassCandidatePlacement: CandidatePlacementMapper = (placement) => ({
    style: toResolvedCompassPlacement(placement),
    reported: placement,
});

/**
 * Runs exactly what the render path runs, so the winning candidate's reservation matches the box that gets
 * drawn — `CallbackCache` keys on the params, so the render pass reuses this result rather than re-invoking
 * the styler.
 *
 * Resolved from the unhighlighted style: hovering must not reflow labels, so a highlight- or
 * selection-dependent styler changes only what is drawn, never where it is placed.
 */
function resolveCandidateLabelStyle<TParams>(
    series: SeriesLike,
    label: PlacementStyledLabel<TParams>,
    params: TParams,
    labelPath: string[] | undefined,
    datum: SeriesNodeDatum,
    placement: CandidatePlacement,
    orientation: AgChartLabelOrientation | undefined
): CandidateLabelStyle {
    const style = getLabelStyles(
        series,
        datum,
        params,
        label,
        false,
        undefined,
        labelPath,
        pickPlacementStyle(label, placement.style),
        { placement: placement.reported, orientation }
    );
    return { font: style, boxPadding: expandLabelBoxExtent(style), hidden: !style.enabled };
}

/**
 * The per-candidate style hook the placement engine resolves label geometry through, or `undefined` when
 * the label has no `itemStyler` and its geometry is therefore placement-invariant.
 */
export function createCandidateStyleResolver<TParams>(
    series: SeriesLike,
    label: PlacementStyledLabel<TParams>,
    params: TParams,
    placementFor: CandidatePlacementMapper,
    labelPath?: string[]
): CandidateStyleResolver | undefined {
    if (label.itemStyler == null) return undefined;
    return function resolveCandidateStyle(datum, placement, orientation) {
        const resolved = placementFor(placement, datum);
        return resolveCandidateLabelStyle(
            series,
            label,
            params,
            labelPath,
            datum as PointLabelDatum & SeriesNodeDatum,
            resolved,
            orientation
        );
    };
}

/**
 * The styled box a bar label reserves on the orientation-only route, where the series bakes the placement
 * and the engine resolves only the rotation. One box is reserved for every orientation there, so it is
 * resolved at `orientation` — the first candidate; a styler that sizes the box per orientation is honoured
 * in what is drawn but not in the reservation. `undefined` for an unstyled label, leaving the configured
 * measurement in place.
 *
 * Callers must drop a `hidden` result from their label data: unlike the cascade route, where the engine
 * skips hidden candidates itself, nothing downstream of here would stop a disabled label reserving space.
 */
export function styledBarLabelBox(
    resolveStyle: BarCandidateStyleResolver | undefined,
    styleDatum: SeriesNodeDatum | undefined,
    placement: BarLabelPlacement,
    orientation: AgChartLabelOrientation,
    text: NormalisedTextOrSegments
): StyledBarLabelBox | undefined {
    if (resolveStyle == null || styleDatum == null) return undefined;
    const { font, boxPadding, hidden } = resolveStyle(styleDatum, placement, orientation);
    const glyph = measureLabelText(text, font);
    return {
        size: {
            width: glyph.width + boxPadding.left + boxPadding.right,
            height: glyph.height + boxPadding.top + boxPadding.bottom,
        },
        font,
        boxPadding,
        hidden: hidden === true,
    };
}

export interface StyledBarLabelBox {
    readonly size: { width: number; height: number };
    readonly font: FontOptions;
    readonly boxPadding: Required<PaddingOptions>;
    /** The styler disabled this label, so it must be left out of the label data entirely. */
    readonly hidden: boolean;
}

/** A bar-family label surface: the placement-styled label every bar/histogram/waterfall/funnel series holds. */
export type BarLabelSurface<TParams = never> = Label<TParams> & {
    insideStyle: LabelPlacementStyle;
    outsideStyle: LabelPlacementStyle;
};

/** The per-series values a bar-family `getLabelData` resolves once before walking its label data. */
export interface BarLabelDataContext {
    /** Per-side extent of the drawn box (padding plus border) around the glyph. */
    readonly box: Required<PaddingOptions>;
    readonly alwaysShow: boolean;
    readonly collideWith: CollideWith;
    readonly threshold: number;
    /** Drawn-box footprint of `text` under the configured label font: the measured glyph plus {@link box}. */
    readonly measureBox: (text: NormalisedTextOrSegments) => { width: number; height: number };
    /** Per-candidate fit inputs for `text`, or `undefined` when the label opted out of overflow control. */
    readonly fitFor: (text: NormalisedTextOrSegments) => LabelFitDescriptor | undefined;
}

/**
 * Resolves the collision and fit inputs shared by every label in a bar-family series. Every such series
 * needs the same six values in the same way, so they are derived here rather than restated per series.
 */
export function barLabelDataContext<TParams>(label: BarLabelSurface<TParams>): BarLabelDataContext {
    // Inflate the measured text by the label's drawn box (padding + border stroke) so collisions
    // avoid the box, not just the text.
    const box = expandPlacementLabelBoxExtent(label);
    const alwaysShow = label.collision.alwaysShow;
    return {
        box,
        alwaysShow,
        collideWith: label.collision.resolveCollideWith(),
        threshold: label.collision.threshold ?? 0,
        measureBox: (text) => {
            const { width, height } = measureLabelText(text, label);
            return { width: width + box.left + box.right, height: height + box.top + box.bottom };
        },
        fitFor: resolveLabelFitDescriptors(label, box, !alwaysShow),
    };
}

/**
 * The obstacles a bar-family series contributes: its bar rects, plus its own baked labels when they are
 * not routed through the placement engine. `pick` names the label on each element, which is the only
 * part that differs between series (a bar hangs its label off the node, a range bar flattens the pair).
 */
export function barLabelObstaclesFor<TParams, TElement>(
    label: BarLabelSurface<TParams>,
    nodeData: readonly RectObstacleSource[] | undefined,
    labelData: Iterable<TElement> | undefined,
    includeBaked: boolean,
    pick: (element: TElement) => BakedBarLabel | undefined
) {
    const box = expandPlacementLabelBoxExtent(label);
    return barLabelObstacles(nodeData, labelData, includeBaked, (element) => {
        const picked = pick(element);
        return { label: picked, config: fontWithSize(label, picked?.fittedFontSize), box };
    });
}

/** A baked bar label, plus the reduced font size a shrink-to-fit pass left it drawn at. */
type BakedBarLabel = NonNullable<BakedLabelSource['label']> & { readonly fittedFontSize?: number };

/** The `itemStyler` geometry of one bar-label candidate; see {@link buildBarLabelCandidates}. */
export type BarCandidateStyleResolver = (
    nodeDatum: SeriesNodeDatum,
    placement: BarLabelPlacement,
    orientation: AgChartLabelOrientation
) => CandidateLabelStyle;

/**
 * The per-candidate style hook for bar-family labels, whose candidates the series positions itself. Unlike
 * the compass path the datum comes in per call, because these candidates are built while the node data is:
 * a series holds one resolver for every label it lays out.
 */
export function createBarCandidateStyleResolver<TParams>(
    series: SeriesLike,
    label: PlacementStyledLabel<TParams>,
    params: TParams,
    labelPath?: string[],
    reportPlacement?: (placement: BarLabelPlacement) => ResolvedPlacement['placement']
): BarCandidateStyleResolver | undefined {
    if (label.itemStyler == null) return undefined;
    return function resolveBarCandidateStyle(nodeDatum, placement, orientation) {
        const resolved = { style: toResolvedPlacement(placement), reported: reportPlacement?.(placement) ?? placement };
        return resolveCandidateLabelStyle(series, label, params, labelPath, nodeDatum, resolved, orientation);
    };
}

export function getLabelStyles<TParams>(
    series: SeriesLike,
    nodeDatum: SeriesNodeDatum | undefined,
    params: TParams,
    label: Label<TParams>,
    isHighlight: boolean,
    activeHighlight: HighlightNodeDatum | undefined,
    labelPath: string[] = ['series', `${series.declarationOrder}`, 'label'],
    placementStyle?: LabelPlacementStyle,
    resolvedPlacement?: ResolvedPlacement
): NormalisedChartLabelStyleOptions & { fontSize: number } {
    const resolvedLabel = resolvePlacementLabelStyle(label, placementStyle);
    if (series.visible && label.itemStyler) {
        const highlightState = series.getHighlightStateString(
            activeHighlight,
            isHighlight ||
                (nodeDatum != null &&
                    activeHighlight?.series === nodeDatum.series &&
                    activeHighlight?.datumIndex === nodeDatum.datumIndex),
            nodeDatum?.datumIndex
        );

        const itemId: string | number | undefined = nodeDatum
            ? getItemId(nodeDatum, series.data?.dataIdKey)
            : undefined;

        const styleParams: NormalisedCallbackParams<
            AgChartLabelStylerParams<unknown, unknown>,
            { color?: CssColor; fontSize: number; fill?: NormalisedColorType }
        > = {
            border: resolvedLabel.border,
            color: resolvedLabel.color,
            cornerRadius: resolvedLabel.cornerRadius,
            datum: nodeDatum?.datum,
            enabled: label.enabled,
            fill: resolvedLabel.fill,
            fillOpacity: resolvedLabel.fillOpacity,
            fontFamily: resolvedLabel.fontFamily,
            fontSize: resolvedLabel.fontSize,
            fontStyle: resolvedLabel.fontStyle,
            fontWeight: resolvedLabel.fontWeight,
            itemId,
            itemType: nodeDatum?.itemType,
            seriesId: series.id,
            padding: resolvedLabel.padding,
            // Present only for series that resolve them, so an unrelated series' styler params are unchanged.
            ...(resolvedPlacement?.placement !== undefined && { placement: resolvedPlacement.placement }),
            ...(resolvedPlacement?.orientation !== undefined && { orientation: resolvedPlacement.orientation }),
            highlightState,
            selectionState: series.getSelectionStateString(nodeDatum?.datumIndex),
            candidateState: series.getCandidateStateString(nodeDatum?.datumIndex),
        };
        const stylerResult =
            series.ctx.optionsGraphService.resolvePartial(
                labelPath,
                series.cachedCallWithContext(label.itemStyler, { ...params, ...styleParams }),
                { pick: false }
            ) ?? {};

        return mergeDefaults(stylerResult, styleParams);
    }

    return resolvedLabel;
}

// Enforce that D must not be `any`
export function updateLabelNode<TParams, D extends LabelDatum>(
    series: IsAny<D> extends false ? SeriesLike : never,
    textNode: IsAny<D> extends false ? Text : never,
    params: IsAny<D> extends false ? TParams : never,
    label: IsAny<D> extends false ? Label<TParams, unknown> : never,
    labelDatum: D | undefined,
    highlight: { isHighlight: boolean; activeHighlight: HighlightNodeDatum | undefined },
    labelPath?: string[],
    placementStyle?: LabelPlacementStyle,
    resolvedPlacement?: ResolvedPlacement
): void;

export function updateLabelNode<TParams>(
    series: SeriesLike,
    textNode: Text<SeriesNodeDatum>,
    params: TParams,
    label: Label<TParams, unknown>,
    labelDatum: LabelDatum | undefined,
    highlight: { isHighlight: boolean; activeHighlight: HighlightNodeDatum | undefined },
    labelPath?: string[],
    placementStyle?: LabelPlacementStyle,
    resolvedPlacement?: ResolvedPlacement
) {
    const { isHighlight, activeHighlight } = highlight;
    if (series.visible && label.enabled && labelDatum) {
        const style = getLabelStyles(
            series,
            textNode.datum,
            params,
            label,
            isHighlight,
            activeHighlight,
            labelPath,
            placementStyle,
            resolvedPlacement
        );
        // A styler that disabled this label hides it, matching the placement engine dropping it.
        if (!style.enabled) {
            textNode.visible = false;
            return;
        }
        textNode.visible = true;
        // Offset slides a rotated bar label flush inside its bar rect; the pivot below is re-derived
        // from the shifted position, so the rotated glyph box moves with it. `0` for every other label.
        textNode.x = labelDatum.x + (labelDatum.offsetX ?? 0);
        textNode.y = labelDatum.y + (labelDatum.offsetY ?? 0);
        textNode.text = labelDatum.fittedText ?? labelDatum.text;
        textNode.fill = style.color;
        textNode.setAlign(labelDatum);
        textNode.setFont(style);
        // Overrides the size `setFont` just applied. Set on the node rather than in `style`, which can be
        // the shared `Label` instance itself.
        if (labelDatum.fittedFontSize != null) {
            textNode.fontSize = labelDatum.fittedFontSize;
        }
        textNode.setBoxing(style);
        if (isRotatable(textNode)) {
            const rotation = labelDatum.rotation ?? 0;
            if (rotation !== 0) {
                // Pivot about the untransformed glyph-box centre so the label rotates in place.
                const bbox = textNode.getTextMeasureBBox();
                textNode.rotationCenterX = bbox.x + bbox.width / 2;
                textNode.rotationCenterY = bbox.y + bbox.height / 2;
            }
            textNode.rotation = rotation;
        }
    } else {
        textNode.visible = false;
    }
}

interface PlacementConfig {
    inside: boolean;
    direction: -1 | 1;
    textAlignment: -1 | 1;
}

const placements: Record<Exclude<BarLabelPlacement, 'inside-center' | BesideBarLabelPlacement>, PlacementConfig> = {
    'inside-start': { inside: true, direction: -1, textAlignment: 1 },
    'inside-end': { inside: true, direction: 1, textAlignment: -1 },
    'outside-start': { inside: false, direction: -1, textAlignment: -1 },
    'outside-end': { inside: false, direction: 1, textAlignment: 1 },
};

function isBesidePlacement(placement: BarLabelPlacement): placement is BesideBarLabelPlacement {
    return placement.startsWith('beside-');
}

/** The value-axis end an inside/beside placement anchors against, so `spacing` reserves its gap there. */
export function barValueAnchor(placement: BarLabelPlacement): BarValueAnchor {
    const value = isBesidePlacement(placement) ? besideValuePlacement(placement).valuePlacement : placement;
    if (value.endsWith('-center')) return 'center';
    return value.endsWith('-start') ? 'start' : 'end';
}

/**
 * Containment region and text container for an inside bar label at `placement`: the region reserves the
 * anchored-side `spacing` gap (nothing for the centred placement), so the gap survives the engine's
 * flush/containment; the container is that region minus the drawn box, bounding how much text fits. Fit
 * and containment share the region so fitted text can never overflow the bound the engine contains it
 * against.
 */
export function insideBarLabelBounds(
    rect: Bounds,
    placement: BarLabelPlacement,
    isUpward: boolean,
    isVertical: boolean,
    spacing: number,
    box: Required<PaddingOptions>
): { region: BoxBounds; container: { width: number; height: number } } {
    const insets = insideBarValueInsets(barValueAnchor(placement), isUpward, isVertical, spacing);
    const region = insideBarRegion(rect, insets.min, insets.max, isVertical);
    return { region, container: insideBarContainer(region, box) };
}

/**
 * Resolves a `beside-*` placement into the side it sits on (`after` = the far side of the cross axis
 * before any reversal) and the along-value-axis anchor it reuses from the matching `inside-*` placement.
 */
function besideValuePlacement(placement: BesideBarLabelPlacement): {
    after: boolean;
    valuePlacement: Exclude<BarLabelPlacement, BesideBarLabelPlacement>;
} {
    const after = placement.includes('-after-');
    let align: 'start' | 'center' | 'end' = 'center';
    if (placement.endsWith('-start')) {
        align = 'start';
    } else if (placement.endsWith('-end')) {
        align = 'end';
    }
    return { after, valuePlacement: `inside-${align}` };
}

export function adjustLabelPlacement({
    isUpward,
    isVertical,
    placement,
    spacing = 0,
    boxPadding,
    rect,
    rotation = 0,
    labelWidth = 0,
    labelHeight = 0,
    crossReversed = false,
}: {
    placement: BarLabelPlacement;
    isUpward: boolean;
    isVertical: boolean;
    spacing?: PixelSize;
    boxPadding?: Required<PaddingOptions>;
    rect: Bounds;
    rotation?: number;
    labelWidth?: number;
    labelHeight?: number;
    crossReversed?: boolean;
}): Omit<LabelDatum, 'text'> {
    let x = rect.x + rect.width / 2;
    let y = rect.y + rect.height / 2;
    let textAlign: CanvasTextAlign = 'center';
    let textBaseline: CanvasTextBaseline = 'middle';

    // The node rotates the padded box about its own centre; asymmetric padding drifts the glyph off the
    // anchor. Pre-subtracting the drift keeps the glyph centred on the bar's cross-axis. Zero unrotated.
    const drift = boxPadding == null ? { x: 0, y: 0 } : rotatedGlyphDrift(rotation, boxPadding);

    // Distance from the anchor to the (rotated) box edge facing the bar; added beyond `spacing` so the box
    // edge — not the text — sits `spacing` from the bar.
    const insetFor = (facing: keyof Required<PaddingOptions>) =>
        boxPadding == null ? 0 : rotatedLabelInset(facing, rotation, labelWidth, labelHeight, boxPadding);

    // `beside-*` reuses the matching `inside-*` anchor for the value axis, then floats the label off the
    // segment on the cross axis (overriding that axis's coordinate and text anchor below).
    let beside: { after: boolean } | undefined;
    let valuePlacement: Exclude<BarLabelPlacement, BesideBarLabelPlacement>;
    if (isBesidePlacement(placement)) {
        const resolved = besideValuePlacement(placement);
        beside = { after: resolved.after };
        valuePlacement = resolved.valuePlacement;
    } else {
        valuePlacement = placement;
    }

    // A beside label is pushed off the bar by `spacing` on its cross axis only (below); along the value
    // axis it just aligns start/center/end to the bar, with no `spacing` gap.
    const valueSpacing = beside == null ? spacing : 0;

    if (valuePlacement === 'inside-center') {
        // No bar-facing axis: keep the glyph centred on both axes of the bar rect.
        x -= drift.x;
        y -= drift.y;
    } else {
        const barDirection = (isUpward ? 1 : -1) * (isVertical ? -1 : 1);
        const { direction, textAlignment } = placements[valuePlacement];
        const displacementRatio = (direction + 1) * 0.5;

        if (isVertical) {
            const y0 = isUpward ? rect.y + rect.height : rect.y;
            const height = rect.height * barDirection;
            const facing = textAlignment === barDirection ? 'top' : 'bottom';
            const inset = insetFor(facing);
            y = y0 + height * displacementRatio + (valueSpacing + inset) * textAlignment * barDirection;
            x -= drift.x;
            textBaseline = facing;
        } else {
            const x0 = isUpward ? rect.x : rect.x + rect.width;
            const width = rect.width * barDirection;
            const facing = textAlignment === barDirection ? 'left' : 'right';
            const inset = insetFor(facing);
            x = x0 + width * displacementRatio + (valueSpacing + inset) * textAlignment * barDirection;
            y -= drift.y;
            textAlign = facing;
        }
    }

    if (beside) {
        // Flip the side with a reversed category axis so `before`/`after` keep their physical meaning
        // (column: before → left; horizontal bar: before → above).
        const after = beside.after !== crossReversed;
        if (isVertical) {
            const facing = after ? 'left' : 'right';
            const offset = spacing + insetFor(facing);
            x = after ? rect.x + rect.width + offset : rect.x - offset;
            textAlign = facing;
        } else {
            const facing = after ? 'top' : 'bottom';
            const offset = spacing + insetFor(facing);
            y = after ? rect.y + rect.height + offset : rect.y - offset;
            textBaseline = facing;
        }
    }

    return { x, y, textAlign, textBaseline };
}

/**
 * Glyph budget a bar label has inside `region` at a given rotation: the region minus its drawn box, with
 * the axes swapped for a rotated label, whose glyph width runs along the region's height.
 */
function orientedBarContainer(region: BoxBounds, rotationDeg: number, box: Required<PaddingOptions>) {
    if (rotationDeg % 180 === 0) return insideBarContainer(region, box);
    return {
        width: Math.max(0, region.height - box.left - box.right),
        height: Math.max(0, region.width - box.top - box.bottom),
    };
}

/**
 * A pre-positioned bar label candidate: the generic {@link PositionedLabelCandidate} box the placement
 * engine cascades over, plus the bar-specific `anchor` and granular `placement` written back onto the
 * label node when this candidate wins (its coarse inside/outside is derived from `placement`).
 */
export interface BarPositionedCandidate<
    TPlacement extends string = BarLabelPlacement,
> extends PositionedLabelCandidate {
    readonly anchor: OrientationAnchor;
    readonly placement: TPlacement;
    /**
     * Inputs {@link createBarPositionedCandidateResolver} rebuilds this candidate from under the style its
     * `itemStyler` resolves there, once the cascade reaches it.
     */
    readonly deferred?: BarCandidateDeferred<TPlacement>;
}

/**
 * Per-label half of a candidate's inputs, shared by every candidate in one label's list. A subset of
 * {@link buildBarLabelCandidates}'s own argument object, which stands in as the spec so that positioning a
 * candidate costs no allocation of its own.
 */
interface BarCandidateSpec {
    readonly isUpward: boolean;
    readonly isVertical: boolean;
    readonly spacing: number;
    readonly crossReversed?: boolean;
    readonly rect: Bounds;
    /** Text size under the configured font, used by a candidate no styler resizes. */
    readonly textWidth: number;
    readonly textHeight: number;
    /** Source text, re-measured under a candidate's styled font; unset leaves the candidate unstyled. */
    readonly text?: NormalisedTextOrSegments;
    readonly fitted?: boolean;
    readonly shapeAt?: (anchor: OrientationAnchor) => FitRegion | undefined;
}

/**
 * What distinguishes one candidate within its label's {@link BarCandidateSpec}: enough to rebuild its
 * box under whatever style the `itemStyler` resolves at this placement and orientation.
 */
export interface BarCandidateDeferred<TPlacement extends string = BarLabelPlacement> {
    readonly spec: BarCandidateSpec;
    readonly placement: BarLabelPlacement;
    readonly reportedPlacement: TPlacement;
    readonly orientation: AgChartLabelOrientation;
    readonly insideRegion: BoxBounds | undefined;
    readonly region: BoxBounds | undefined;
    /** Drawn-box extent of the configured style at this placement, used when nothing restyles it. */
    readonly placementBox: Required<PaddingOptions>;
}

/**
 * The room a shape offers, held clear of its edge by the box drawn around the glyph. A region is anchored
 * on its own centre line, so each axis pays the larger of its two paddings.
 */
function insetShapeRegion(shape: FitRegion | undefined, boxPadding: Required<PaddingOptions>) {
    if (shape == null) return undefined;
    const dx = Math.max(boxPadding.left, boxPadding.right);
    const dy = Math.max(boxPadding.top, boxPadding.bottom);
    return dx === 0 && dy === 0 ? shape : insetFitRegion(shape, dx, dy);
}

/** Where a block of text hangs off its anchor, read from the baseline the anchor draws it on. */
function regionAlignOf(textBaseline: CanvasTextBaseline): RegionAlign {
    if (textBaseline === 'top' || textBaseline === 'hanging') return 'start';
    if (textBaseline === 'bottom' || textBaseline === 'alphabetic') return 'end';
    return 'center';
}

/**
 * Builds the ordered candidate list a bar label cascades through, one entry per
 * `placement` (outer) × `orientation` (inner) — the ordering that yields inside-horizontal →
 * inside-vertical → outside-horizontal → outside-vertical for the ticket's example. Inside placements
 * constrain to the inset bar rect; outside placements float (no region).
 *
 * With `fitted` set, each candidate also carries the glyph budget its region offers, so the placement
 * engine re-fits the text per candidate instead of every candidate inheriting one up-front truncation.
 *
 * Every candidate is built on the configured style, and a label with an `itemStyler` also carries the
 * inputs to rebuild itself under the geometry that styler resolves, which the placement engine reaches for
 * only as the cascade reaches that candidate (see {@link createBarPositionedCandidateResolver}). A label
 * the engine never sees must resolve its own candidate through {@link resolveBarLabelCandidate}.
 */
export function buildBarLabelCandidates<TParams, TPlacement extends string = BarLabelPlacement>(args: {
    isUpward: boolean;
    isVertical: boolean;
    placements: readonly BarLabelPlacement[];
    /**
     * Public placement values, index-parallel with `placements`, reported on the winning candidate in
     * place of the bar vocabulary a series maps onto.
     */
    reportedPlacements?: readonly TPlacement[];
    orientations: readonly AgChartLabelOrientation[];
    spacing: number;
    // The styled label; the box extent (padding + border) is resolved per candidate from its placement's
    // style, so an inside↔outside cascade offsets and sizes each candidate by its own style.
    label: Label<TParams> & { insideStyle: LabelPlacementStyle; outsideStyle: LabelPlacementStyle };
    // Raw measured text size, before the per-placement box extent is folded in.
    textWidth: number;
    textHeight: number;
    rect: Bounds;
    /**
     * Supplies the cross-axis extent of the inside regions, for a `rect` with no thickness of its own
     * (a divider line), whose inside region would otherwise reject every candidate. Containment along
     * the rect is unaffected.
     */
    insideCrossRegion?: Bounds;
    crossReversed?: boolean;
    rejectOutsideStart?: boolean;
    rejectOutsideEnd?: boolean;
    /** A label that may be dropped on overflow (`collision.alwaysShow: false`) rather than always rendered. */
    hideable?: boolean;
    plotRegion?: Bounds;
    /** Attach the per-candidate fit inputs the engine needs to re-fit the text to each candidate. */
    fitted?: boolean;
    /**
     * The shape an inside candidate's text is fitted to, for a series whose rect is only the bounding box
     * of a non-rectangular mark. Each line is then wrapped to the width the shape offers where it lands.
     */
    shapeAt?: (anchor: OrientationAnchor) => FitRegion | undefined;
    /** Source text, re-measured under a candidate's styled font; unset leaves every candidate unstyled. */
    text?: NormalisedTextOrSegments;
}): BarPositionedCandidate<TPlacement>[] {
    const {
        isUpward,
        isVertical,
        placements: placementList,
        reportedPlacements,
        orientations,
        spacing,
        label,
        rect,
        insideCrossRegion,
        rejectOutsideStart = false,
        rejectOutsideEnd = false,
        hideable = false,
        plotRegion,
        text,
    } = args;
    // Drop the outside placements that would point into an adjacent stacked segment on that side, so the
    // cascade falls through to a beside/inside candidate rather than mislabelling the neighbour.
    const rejectsOutside = rejectOutsideStart || rejectOutsideEnd;
    let effectiveIndices = placementList.map((_, index) => index);
    if (rejectsOutside) {
        effectiveIndices = effectiveIndices.filter(
            (index) =>
                !(placementList[index] === 'outside-start' && rejectOutsideStart) &&
                !(placementList[index] === 'outside-end' && rejectOutsideEnd)
        );
        if (effectiveIndices.length === 0) {
            // Nowhere left to put it, so drop a hideable label; one that must be shown needs a placement anyway.
            if (hideable) return [];
            effectiveIndices = placementList.map((_, index) => index);
        }
    }

    // `plotRegion` is a collision-only boundary for outside/beside candidates: a label overflowing it fails
    // containment so the cascade falls through, rather than being clamped into it.
    // Nothing restyles a label with no `itemStyler`, so its candidates are final as built.
    const restyleable = label.itemStyler != null && text != null;
    const candidates: BarPositionedCandidate<TPlacement>[] = [];
    for (const placementIndex of effectiveIndices) {
        const placement = placementList[placementIndex];
        const reportedPlacement = (reportedPlacements?.[placementIndex] ?? placement) as TPlacement;
        // Per-placement drawn-box extent: an inside candidate uses insideStyle, an outside one outsideStyle,
        // so their padding/border differences move the anchor and size the footprint independently.
        const placementBox = resolvePlacementLabelBoxExtent(
            label,
            pickPlacementStyle(label, toResolvedPlacement(placement))
        );
        const isInside = placement.startsWith('inside');
        // Inside labels reserve `spacing` on the end they anchor against, so the gap survives the engine's
        // flush/containment (not just the anchor); centred labels reserve nothing.
        const insets = insideBarValueInsets(barValueAnchor(placement), isUpward, isVertical, spacing);
        let insideRegion = isInside ? insideBarRegion(rect, insets.min, insets.max, isVertical) : undefined;
        if (insideRegion != null && insideCrossRegion != null) {
            insideRegion = isVertical
                ? { ...insideRegion, x: insideCrossRegion.x, width: insideCrossRegion.width }
                : { ...insideRegion, y: insideCrossRegion.y, height: insideCrossRegion.height };
        }
        const region = insideRegion ?? plotRegion;
        for (const orientation of orientations) {
            // Node data outlives the render, so only a candidate something can still restyle carries the
            // inputs to rebuild itself; every other label's are dead as its candidate is built.
            const deferred = restyleable
                ? { spec: args, placement, reportedPlacement, orientation, insideRegion, region, placementBox }
                : undefined;
            candidates.push(
                positionBarLabelCandidate(
                    args,
                    placement,
                    reportedPlacement,
                    orientation,
                    insideRegion,
                    region,
                    placementBox,
                    undefined,
                    deferred
                )
            );
        }
    }
    return candidates;
}

/**
 * One candidate's geometry under `style`, or under the configured style when it is unset. A styler can
 * change the font and box per orientation as well as per placement, so the glyph, the box extent and the
 * anchor they position are all derived here.
 *
 * `deferred` is what the candidate carries for a later restyle, retained as passed. The inputs arrive
 * positionally, and not as that one object, because this runs per candidate of every bar label of every
 * datum, where a label with no `itemStyler` has nothing to retain.
 */
function positionBarLabelCandidate<TPlacement extends string>(
    spec: BarCandidateSpec,
    placement: BarLabelPlacement,
    reportedPlacement: TPlacement,
    orientation: AgChartLabelOrientation,
    insideRegion: BoxBounds | undefined,
    region: BoxBounds | undefined,
    placementBox: Required<PaddingOptions>,
    style: CandidateLabelStyle | undefined,
    deferred: BarCandidateDeferred<TPlacement> | undefined
): BarPositionedCandidate<TPlacement> {
    const { text, shapeAt } = spec;
    const isInside = placement.startsWith('inside');
    const boxPadding = style?.boxPadding ?? placementBox;
    const glyph = style == null || text == null ? undefined : measureLabelText(text, style.font);
    const width = (glyph?.width ?? spec.textWidth) + boxPadding.left + boxPadding.right;
    const height = (glyph?.height ?? spec.textHeight) + boxPadding.top + boxPadding.bottom;
    const anchor = adjustLabelPlacement({
        isUpward: spec.isUpward,
        isVertical: spec.isVertical,
        placement,
        spacing: spec.spacing,
        boxPadding,
        rect: spec.rect,
        crossReversed: spec.crossReversed ?? false,
    });
    const centre = writeLabelBoxCentre({ x: 0, y: 0 }, anchor, width, height, boxPadding);
    const rotationDeg = orientationAngles[orientation];
    const { width: fw, height: fh } = getMinOuterRectSize(rotationDeg, width, height);
    return {
        box: { x: centre.x - fw / 2, y: centre.y - fh / 2, width: fw, height: fh },
        region,
        flushToRegion: isInside ? undefined : false,
        rotation: rotationDeg || undefined,
        anchor,
        placement: reportedPlacement,
        size: style == null ? undefined : { width, height },
        hidden: style?.hidden,
        // An outside candidate floats, so it offers no container and only the label's own
        // maxWidth/maxHeight can truncate it.
        fitTo: spec.fitted
            ? {
                  container: insideRegion && orientedBarContainer(insideRegion, rotationDeg, boxPadding),
                  // The container is deflated by the box drawn around the glyph, and the shape has
                  // to be held clear of its edge by the same amount, or a line wrapped to the full
                  // width of a taper draws its padding and border outside the mark.
                  shape: isInside ? insetShapeRegion(shapeAt?.(anchor), boxPadding) : undefined,
                  shapeAlign: regionAlignOf(anchor.textBaseline),
                  anchor,
                  padding: boxPadding,
                  font: style?.font,
              }
            : undefined,
        deferred,
    };
}

/** {@link positionBarLabelCandidate} for a candidate rebuilt under `style` from its retained inputs. */
function restyleBarLabelCandidate<TPlacement extends string>(
    deferred: BarCandidateDeferred<TPlacement>,
    style: CandidateLabelStyle | undefined
): BarPositionedCandidate<TPlacement> {
    const { spec, placement, reportedPlacement, orientation, insideRegion, region, placementBox } = deferred;
    return positionBarLabelCandidate(
        spec,
        placement,
        reportedPlacement,
        orientation,
        insideRegion,
        region,
        placementBox,
        style,
        undefined
    );
}

/**
 * One candidate rebuilt under the style its `itemStyler` resolves there, for a label that never reaches
 * the placement engine: its caller holds the only candidate, so nothing else would run its styler.
 * Unchanged when nothing can restyle it.
 */
export function resolveBarLabelCandidate<TPlacement extends string>(
    candidate: BarPositionedCandidate<TPlacement>,
    styleDatum: SeriesNodeDatum,
    resolveStyle: BarCandidateStyleResolver | undefined
): BarPositionedCandidate<TPlacement> {
    const { deferred } = candidate;
    if (resolveStyle == null || deferred?.spec.text == null) return candidate;
    return restyleBarLabelCandidate(deferred, resolveStyle(styleDatum, deferred.placement, deferred.orientation));
}

/**
 * The hook the placement engine resolves a bar label's candidates through — the positioned-candidate
 * counterpart of {@link createCandidateStyleResolver}, and the only route by which a cascading bar label's
 * `itemStyler` runs. `paramsFor` takes the label's own node, for the series whose styler params carry that
 * node's values.
 */
export function createBarPositionedCandidateResolver<TParams>(
    series: SeriesLike,
    label: PlacementStyledLabel<TParams>,
    paramsFor: (styleDatum: SeriesNodeDatum) => TParams,
    labelPath?: string[]
): PositionedCandidateResolver | undefined {
    if (label.itemStyler == null) return undefined;
    return function resolveCandidate(datum, candidate) {
        const { styleDatum } = datum as BarPlacedLabelDatum<SeriesNodeDatum>;
        const { deferred } = candidate as BarPositionedCandidate<string>;
        if (styleDatum == null || deferred?.spec.text == null) return candidate;
        const style = resolveCandidateLabelStyle(
            series,
            label,
            paramsFor(styleDatum),
            labelPath,
            styleDatum,
            // The candidate carries the placement its series promised the styler, which is not always the
            // bar placement its geometry runs on: the funnel family reports its own vocabulary.
            {
                style: toResolvedPlacement(deferred.placement),
                reported: deferred.reportedPlacement as ResolvedPlacement['placement'],
            },
            deferred.orientation
        );
        return restyleBarLabelCandidate(deferred, style);
    };
}
